import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CommonRoute } from '../../../../common/enums/common-route.enum';
import { AppRefreshCoordinator } from '../../../core/visibility/app-refresh-coordinator.service';
import { RefreshKey } from '../../../core/visibility/refresh-key.enum';
import { SupportAvailabilityService } from '../services/support-availability.service';

/**
 * Keeps the Help & Support routes unreachable while the feature is switched off.
 *
 * Hiding the menu entry is not enough on its own — a deep link, a restored tab
 * or browser history all reach the URL directly, and every endpoint behind it
 * answers 404, so the screen would render and then fail on load.
 *
 * Pulls the flag when a resume, a session change or age has made it stale
 * (ADR-0264) — a pull joins the boot or sign-in check still in flight, so a
 * direct navigation straight after either waits for its answer. A closed answer
 * is asked again before refusing: this route is rare, and a check that failed
 * closed must not strand a user who followed a real link.
 */
export const SupportEnabledGuard: CanActivateFn = async () => {
  const availability = inject(SupportAvailabilityService);
  const coordinator = inject(AppRefreshCoordinator);
  const router = inject(Router);

  await coordinator.refreshIfStale(RefreshKey.SupportAvailability);
  if (availability.enabled() || (await availability.refresh())) {
    return true;
  }

  return router.createUrlTree([CommonRoute.NOT_FOUND]);
};
